import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/primitives/Input';
import { Toggle } from '@/components/primitives/Toggle';
import { PageTitle } from '@/components/PageTitle';
import { SaveBar, SettingsGroup, SettingsLoading, SettingsNote, SettingsRow, } from '@/components/settings/Settings';
import { ApiError } from '@/api/client';
import { apiErrorText } from '@/api/opengater/errors';
import { useOgAuth } from '@/api/opengater/OgAuthContext';
import f from './OpengaterForm.module.css';
const MONEY_RE = /^\d+(\.\d{1,2})?$/;
const INT_RE = /^-?\d+$/;
function fieldValid(fl, raw) {
    if (fl.kind === 'bool')
        return true;
    const v = raw.trim();
    if (v === '')
        return !!fl.optional; // required unless optional (→ null)
    if (fl.kind === 'money')
        return MONEY_RE.test(v);
    if (fl.kind === 'int') {
        if (!INT_RE.test(v))
            return false;
        const n = Number(v);
        if (fl.min !== undefined && n < fl.min)
            return false;
        if (fl.max !== undefined && n > fl.max)
            return false;
        return true;
    }
    return true; // text / url — non-empty is enough; server validates format
}
function fieldError(fl) {
    if (fl.kind === 'money')
        return 'Число, до 2 знаков';
    if (fl.kind === 'int') {
        if (fl.min !== undefined && fl.max !== undefined)
            return `Целое ${fl.min}–${fl.max}`;
        return 'Целое число';
    }
    return 'Обязательное поле';
}
export function ConfigFormScreen(props) {
    const { allowed, markDenied } = useOgAuth();
    const canEdit = allowed(props.capability, 'admin');
    const fields = useMemo(() => props.sections.flatMap((sec) => sec.fields), [props.sections]);
    const base = useMemo(() => {
        if (!props.data)
            return null;
        const o = {};
        for (const fl of fields) {
            const v = props.data[fl.key];
            o[fl.key] = v === null || v === undefined ? '' : String(v);
        }
        return o;
    }, [props.data, fields]);
    const [edits, setEdits] = useState({});
    const draft = useMemo(() => ({ ...(base ?? {}), ...edits }), [base, edits]);
    const dirty = base ? fields.some((fl) => (draft[fl.key] ?? '') !== base[fl.key]) : false;
    const invalidCount = fields.filter((fl) => !fieldValid(fl, draft[fl.key] ?? '')).length;
    const canSave = canEdit && dirty && invalidCount === 0 && !props.saving;
    // Clear the local edit overlay once the server confirms a save — draft then
    // equals the fresh server row and the form reads clean.
    useEffect(() => {
        if (props.saved)
            setEdits({});
    }, [props.saved]);
    // Reactive gate: a 403 on save means this operator can't write here.
    useEffect(() => {
        if (props.saveError instanceof ApiError && props.saveError.status === 403) {
            markDenied(props.capability);
        }
    }, [props.saveError, props.capability, markDenied]);
    const onSubmit = (e) => {
        e.preventDefault();
        if (!canSave)
            return;
        const patch = {};
        for (const fl of fields) {
            const raw = draft[fl.key] ?? '';
            const v = raw.trim();
            if (fl.kind === 'bool')
                patch[fl.key] = raw === 'true';
            else if (fl.kind === 'int')
                patch[fl.key] = Number(v);
            else
                patch[fl.key] = fl.optional && v === '' ? null : v;
        }
        props.onSave(patch);
    };
    const onReset = () => {
        setEdits({});
        props.onResetFeedback?.();
    };
    return (_jsxs(_Fragment, { children: [!props.embedded && _jsx(PageTitle, { title: props.title, sub: props.subtitle }), _jsxs("div", { className: f.page, children: [props.isLoading && _jsx(SettingsLoading, {}), !!props.error && !props.isLoading && (_jsx(SettingsNote, { tone: "error", children: apiErrorText(props.error, 'Не удалось загрузить настройки.') })), base && (_jsxs("form", { onSubmit: onSubmit, children: [props.notice && _jsx(SettingsNote, { tone: "warn", children: props.notice }), props.warnings && props.warnings.length > 0 && (_jsxs(SettingsNote, { tone: "warn", children: ["\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u043D\u0435 \u0441\u0445\u043E\u0434\u044F\u0442\u0441\u044F:", _jsx("ul", { children: props.warnings.map((w, i) => (_jsx("li", { children: w }, i))) })] })), !canEdit && (_jsx(SettingsNote, { tone: "lock", children: "\u0422\u043E\u043B\u044C\u043A\u043E \u043F\u0440\u043E\u0441\u043C\u043E\u0442\u0440: \u0441\u0435\u0440\u0432\u0435\u0440 \u043E\u0442\u043A\u043B\u043E\u043D\u0438\u043B \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u0435 \u2014 \u043E\u043D\u043E \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u043E \u0440\u043E\u043B\u0438 admin." })), props.sections.map((sec, i) => (_jsx(SettingsGroup, { title: sec.title, children: sec.fields.map((fl) => {
                                    const val = draft[fl.key] ?? '';
                                    if (fl.kind === 'bool') {
                                        return (_jsx(SettingsRow, { label: fl.label, hint: fl.hint, children: _jsx(Toggle, { on: val === 'true', disabled: !canEdit || props.saving, onChange: (next) => setEdits((p) => ({ ...p, [fl.key]: next ? 'true' : 'false' })), "aria-label": fl.label }) }, fl.key));
                                    }
                                    const bad = val.trim() !== '' && !fieldValid(fl, val);
                                    return (_jsx(SettingsRow, { label: fl.label, hint: fl.hint, suffix: fl.suffix, wide: fl.kind === 'text' || fl.kind === 'url', error: bad ? fieldError(fl) : undefined, children: _jsx(Input, { value: val, type: fl.kind === 'url' ? 'url' : 'text', inputMode: fl.kind === 'int'
                                                ? 'numeric'
                                                : fl.kind === 'money'
                                                    ? 'decimal'
                                                    : fl.kind === 'url'
                                                        ? 'url'
                                                        : 'text', mono: fl.kind === 'money' || fl.kind === 'int', placeholder: fl.placeholder, disabled: !canEdit || props.saving, "aria-label": fl.label, "aria-invalid": bad || undefined, className: bad ? f.inputBad : undefined, onChange: (e) => setEdits((p) => ({ ...p, [fl.key]: e.target.value })) }) }, fl.key));
                                }) }, sec.title ?? i))), canEdit && (_jsx(SaveBar, { dirty: dirty, saving: props.saving, saved: props.saved, canSave: canSave, error: props.saveError ? apiErrorText(props.saveError) : undefined, onReset: onReset }))] }))] })] }));
}
